import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useParams, Link } from 'react-router-dom';
import { Calendar, ChevronLeft, BookOpen } from 'lucide-react';
import Header from '../components/Header';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function useSEO({ title, description, canonical, ogImage }) {
  useEffect(() => {
    if (title) document.title = title;
    const setMeta = (name, content, attr = 'name') => {
      if (!content) return;
      let el = document.head.querySelector(`meta[${attr}="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };
    setMeta('description', description);
    setMeta('og:title', title, 'property');
    setMeta('og:description', description, 'property');
    setMeta('og:type', 'article', 'property');
    if (ogImage) setMeta('og:image', ogImage, 'property');
    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', title);
    setMeta('twitter:description', description);
    if (canonical) {
      let link = document.head.querySelector('link[rel="canonical"]');
      if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', 'canonical');
        document.head.appendChild(link);
      }
      link.setAttribute('href', canonical);
    }
  }, [title, description, canonical, ogImage]);
}

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/api/blogs/${slug}`);
        setPost(res.data);
      } catch (e) {
        setError('Blog post not found');
      }
      setLoading(false);
    })();
  }, [slug]);

  const canonical = typeof window !== 'undefined' ? `${window.location.origin}/blogs/${slug}` : '';
  const image = post?.featured_image_path ? `${API_URL}/api/public/files/${post.featured_image_path}` : '';

  useSEO({
    title: post ? `${post.title} — Kagajbook Blog` : 'Blog — Kagajbook',
    description: post?.meta_description || '',
    canonical,
    ogImage: image,
  });

  if (loading) return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="text-center py-20 text-[#64748B]">Loading…</div>
    </div>
  );

  if (error || !post) return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="max-w-3xl mx-auto text-center py-20 px-4">
        <BookOpen className="w-12 h-12 mx-auto text-[#F5B800] mb-4" />
        <h1 className="text-2xl font-bold text-[#172554] mb-2">Blog Not Found</h1>
        <p className="text-[#64748B] text-gujarati mb-6">આ લેખ ઉપલબ્ધ નથી અથવા હટાવવામાં આવ્યો છે.</p>
        <Link to="/blogs" className="inline-flex items-center gap-2 bg-[#F5B800] text-[#172554] font-bold px-5 py-2.5 rounded-lg hover:bg-[#FFC928]">
          <ChevronLeft className="w-4 h-4" /> Back to Blogs
        </Link>
      </div>
    </div>
  );

  const formatDate = (iso) => {
    try { return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' }); }
    catch { return ''; }
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="blog-post-page">
      <Header />
      <article className="max-w-3xl mx-auto px-4 py-10">
        <Link to="/blogs" className="inline-flex items-center gap-1 text-sm font-bold text-[#172554] hover:text-[#F5B800] mb-6">
          <ChevronLeft className="w-4 h-4" /> All Blogs
        </Link>
        <h1 className="text-3xl md:text-4xl font-extrabold text-[#172554] leading-tight text-gujarati" data-testid="blog-post-title">
          {post.title}
        </h1>
        <div className="flex items-center gap-2 text-xs text-[#64748B] mt-3">
          <Calendar className="w-3.5 h-3.5" /> {formatDate(post.created_at)}
        </div>
        {post.featured_image_path && (
          <div className="mt-6 rounded-xl overflow-hidden shadow-md bg-gray-100">
            <img src={image} alt={post.title} className="w-full aspect-[16/9] object-cover" />
          </div>
        )}
        {post.meta_description && (
          <p className="mt-6 text-lg text-[#334155] font-medium text-gujarati leading-relaxed border-l-4 border-[#F5B800] pl-4">
            {post.meta_description}
          </p>
        )}
        <div
          className="mt-6 prose prose-slate max-w-none text-[#172033] text-gujarati whitespace-pre-wrap leading-relaxed"
          data-testid="blog-post-body"
        >
          {post.body}
        </div>
      </article>
    </div>
  );
}
